// @generated by `pnpm content:generate` from src/content-export — DO NOT EDIT
import type { TravelerPack } from '../types.js'

export const US: TravelerPack = {
  jurisdiction: {
    id: 'US',
    type: 'country',
    name: 'United States',
  },
  metros: [
    {
      id: 'US-NYC',
      type: 'metro',
      name: 'New York City',
      parent: 'US',
    },
    {
      id: 'US-LAX',
      type: 'metro',
      name: 'Los Angeles',
      parent: 'US',
    },
    {
      id: 'US-MIA',
      type: 'metro',
      name: 'Miami',
      parent: 'US',
    },
  ],
  quickFacts: {
    driveSide: 'right',
    motorwayLimitKmh: 113,
    alcoholLimit: '0.08% BAC (0.0 under 21 / commercial)',
    vignette: 'None — cashless tolls (E-ZPass / SunPass / FasTrak)',
    emergency: '911',
  },
  rules: [
    {
      id: 'us-cashless-tolls',
      target: 'US',
      category: 'vignette_toll',
      severity: 'high',
      title: 'Cashless tolls bill your rental — with admin fees',
      detail: 'Many highways, bridges and express lanes are all-electronic: a camera reads your plate (E-ZPass NE/Midwest, SunPass FL, FasTrak CA) and the rental company bills the toll PLUS a daily admin fee that can dwarf the toll. Decide a toll plan at pickup.',
      cost: 'toll + daily admin fee',
      citation: 'https://www.fhwa.dot.gov/policyinformation/tollpage/',
      lastReviewed: '2026-06-08',
    },
    {
      id: 'us-school-bus',
      target: 'US',
      category: 'limit',
      severity: 'high',
      title: 'Stop for a stopped school bus — usually both directions',
      detail: 'When a school bus stops with red lights flashing and its stop-arm out, you must stop, usually in BOTH directions (varies slightly by state). Heavily enforced; a classic visitor mistake.',
      citation: 'https://www.usa.gov/non-citizen-driving',
      lastReviewed: '2026-06-08',
    },
    {
      id: 'us-right-on-red',
      target: 'US',
      category: 'limit',
      severity: 'medium',
      title: 'Right turn on red — except where banned',
      detail: 'Allowed in most states after a full stop if clear, UNLESS a "No Turn on Red" sign is posted. New York City bans it everywhere unless a sign permits.',
      citation: 'https://www.usa.gov/non-citizen-driving',
      lastReviewed: '2026-06-08',
    },
    {
      id: 'us-hov',
      target: 'US',
      category: 'limit',
      severity: 'medium',
      title: 'HOV / carpool (diamond) lanes need minimum occupancy',
      detail: 'Lanes marked with a diamond require a minimum number of occupants — do not use them driving solo.',
      citation: 'https://www.usa.gov/non-citizen-driving',
      lastReviewed: '2026-06-08',
    },
    {
      id: 'us-mph',
      target: 'US',
      category: 'limit',
      severity: 'info',
      title: 'Speeds are in mph, distances are huge',
      detail: '~25–35 mph in town, 55–75 on highways (80+ on some western interstates). Cities are far apart — fly between and rent locally; budget for traffic and summer heat.',
      citation: 'https://www.usa.gov/non-citizen-driving',
      lastReviewed: '2026-06-08',
    },
    {
      id: 'us-idp-script',
      target: 'US',
      category: 'document',
      severity: 'high',
      title: 'IDP required only if your licence is not in English/Roman script',
      detail: 'Visitors may drive on a valid foreign licence; an International Driving Permit is required if the licence is not in English/Roman script (carry both). Get it before you travel — the US does not issue them. Rentals need licence + passport + credit card and often a driver 25+.',
      citation: 'https://www.usa.gov/non-citizen-driving',
      lastReviewed: '2026-06-08',
    },
  ],
  checklists: {
    pre: [
      {
        id: 'us-pre-idp',
        label: 'Get an IDP if your licence is not in English/Roman script',
        detail: 'From your own national government, before you travel — the US does not issue them.',
        citation: 'https://www.usa.gov/non-citizen-driving',
        lastReviewed: '2026-06-08',
      },
      {
        id: 'us-pre-toll',
        label: 'Decide a rental toll plan at pickup',
        detail: 'Ask how cashless tolls are billed and whether to take the transponder program; admin fees stack up.',
      },
      {
        id: 'us-pre-age',
        label: 'Check the rental age rule',
        detail: 'Many firms require the driver to be 25+; a young-driver surcharge applies under 25.',
      },
      {
        id: 'us-pre-offline',
        label: 'Download offline maps + your route',
        detail: 'Coverage is patchy on long rural/desert legs; save maps before you lose signal.',
      },
    ],
    on: [
      {
        id: 'us-on-bus',
        label: 'Stop for stopped school buses',
        detail: 'Red lights + stop-arm = stop, usually both directions, until the lights go off.',
      },
      {
        id: 'us-on-red',
        label: 'Right-on-red only where allowed',
        detail: 'Full stop first; never where a sign forbids it; never in NYC unless signed.',
      },
      {
        id: 'us-on-hov',
        label: 'Stay out of HOV/diamond lanes solo',
        detail: 'Minimum-occupancy lanes; solo use is ticketed.',
      },
    ],
    post: [
      {
        id: 'us-post-tolls',
        label: 'Settle toll/camera notices promptly',
        detail: 'They route via the rental company with fees weeks later — do not ignore them.',
      },
      {
        id: 'us-post-dispute',
        label: 'Keep your rental agreement + dates',
        detail: 'In case a ticket or toll is misattributed after you return.',
      },
    ],
  },
  course: {
    title: 'Driving in the USA',
    modules: [
      {
        id: 'us-m1-licence',
        title: 'Licence, IDP & renting',
        minutes: 6,
        body: 'The US drives on the right. Visitors may drive on a valid home-country licence; an International Driving Permit is required only if your licence is not in English/Roman script — and even then it is just a translation you carry WITH the original. Get the IDP from your own government before you travel; the US does not issue them. Rental firms rent on a foreign licence + passport + credit card, but often require the driver to be 25+ (a young-driver surcharge applies under 25) and may ask for an IDP regardless.',
        keyPoints: [
          'Drive on a valid foreign licence; IDP only if not in English/Roman script',
          'The IDP is a translation — carry the original licence too',
          'Rentals: passport + credit card; often 25+',
        ],
      },
      {
        id: 'us-m2-tolls',
        title: 'Cashless tolls & the rental fee trap',
        minutes: 5,
        body: 'Many highways, bridges and express lanes are all-electronic — no booths; a camera reads your plate (E-ZPass in the Northeast/Midwest, SunPass in Florida, FasTrak in California). In a rental, the charge routes to the company, which bills your card the toll PLUS a daily admin fee that can dwarf the toll itself. Decide your toll plan at pickup: take the transponder program or ask exactly how toll-by-plate is billed. This is the US version of the surprise fine that arrives weeks later.',
        keyPoints: [
          'Cashless tolls have no booths — your plate is billed',
          'Rentals add a daily admin fee on top of the toll',
          'Choose a toll plan at the rental desk',
        ],
        check: {
          q: 'You breeze through a cashless toll in a rental — what happens?',
          options: [
            {
              label: 'The toll + a rental admin fee are billed to your card later',
              correct: true,
            },
            {
              label: 'Nothing — rentals are exempt from tolls',
              correct: false,
            },
          ],
        },
      },
      {
        id: 'us-m3-rules',
        title: 'The rules that catch visitors',
        minutes: 6,
        body: 'Right turn on red is allowed in most states after a full stop if clear — unless a "No Turn on Red" sign is posted; New York City bans it everywhere unless a sign permits. When a school bus stops with red lights flashing and its stop-arm out, you must stop — usually in both directions, and stay stopped until the lights go off; this is heavily enforced. At four-way stops, first to arrive goes first; if simultaneous, yield to the right. HOV/carpool lanes (often a diamond) need a minimum number of occupants. Speeds are in mph, and there\'s no blanket ban on passing on the right on a multi-lane road — but it isn\'t a right-of-way, so keep right except to pass.',
        keyPoints: [
          'Right-on-red after a full stop unless signed; NYC bans it',
          'Stopped school bus = stop, usually both directions',
          'HOV/diamond lanes need minimum occupancy',
        ],
      },
      {
        id: 'us-m4-speed',
        title: 'Speed, alcohol & distances',
        minutes: 5,
        body: 'Speeds are in mph: ~25–35 in town, 55–75 on highways, 80+ on some western interstates — and enforcement varies by state. The alcohol limit is 0.08% BAC nationwide, with zero tolerance for under-21 and commercial drivers; penalties are severe and for a visitor a DUI can affect future entry to the US. Distances are huge (Dallas–Houston ~240 mi, LA–SF ~380 mi): for an event trip, fly city-to-city and rent locally, and budget for traffic and summer heat.',
        keyPoints: [
          'mph, not km/h; limits and enforcement vary by state',
          '0.08% BAC; zero tolerance under 21 — a DUI can affect entry',
          'Distances are huge — fly between cities, rent locally',
        ],
        check: {
          q: 'The US national drink-drive limit is…',
          options: [
            {
              label: '0.08% BAC (zero tolerance under 21)',
              correct: true,
            },
            {
              label: '0.05% BAC everywhere',
              correct: false,
            },
          ],
        },
      },
      {
        id: 'us-m5-events',
        title: 'Crowds, events & if something goes wrong',
        minutes: 5,
        body: 'For big events (World Cup matches, July-4 / America250 crowds, festival weekends), stadium and downtown parking is limited, pricey and often pre-book only, with road closures around venues — use transit, park-and-ride or rideshare where available, and treat "drive to the gate" as the wrong move. RoadBridge tells you when NOT to drive, then helps with the driving you do (airport runs, intercity legs, day trips). In an emergency dial 911; pull right for emergency vehicles; keep your licence, rental agreement and insurance in the car. Tickets, tolls and camera notices route via the rental company with fees — handle them promptly.',
        keyPoints: [
          'Event days: use transit/park-and-ride/rideshare, not the stadium gate',
          'Emergency = 911; keep documents in the car',
          'Post-trip notices route via the rental company — settle them',
        ],
      },
    ],
  },
  emergency: {
    number: '911',
    steps: [
      {
        title: 'In an emergency',
        detail: 'Dial 911 for police, ambulance or fire. Give your location (highway + direction, or nearest exit/cross-street) and the number of people hurt.',
      },
      {
        title: 'After a collision',
        detail: 'Move to safety if you can, switch on hazards, and exchange licence, insurance and rental details. Photograph positions, plates and damage.',
      },
      {
        title: 'Emergency vehicles',
        detail: 'Pull to the right and stop for approaching sirens/lights; do not block intersections.',
      },
    ],
    phrases: [
      {
        en: 'I need an ambulance',
        local: 'Call 911 (say "nine-one-one") and ask for an ambulance',
      },
      {
        en: 'I have been in a car accident',
        local: 'Call 911 — police, fire and ambulance share the one number',
      },
      {
        en: 'Where is the nearest hospital?',
        local: 'Ask for the nearest "ER" / "emergency room"',
      },
    ],
  },
}
